import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useIpPool } from '@/hooks/useIpPool'
import { formatRelativeTime } from '@/lib/utils'
import type { UnifiedPoolIpRow } from '@/api/types'

// Cloud pool status colors
const cloudStatusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning'> = {
  available: 'success',
  reserved: 'warning',
  assigned: 'default',
  blacklisted: 'destructive',
}

// Local pool status colors
const localStatusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning'> = {
  ready: 'success',
  pending: 'warning',
  active: 'default',
  blocked: 'destructive',
}

const riskVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning'> = {
  low: 'success',
  medium: 'warning',
  high: 'destructive',
}

function getStatusVariant(ip: UnifiedPoolIpRow): 'default' | 'secondary' | 'destructive' | 'success' | 'warning' {
  if (ip.source === 'cloud') {
    return cloudStatusVariants[ip.status] ?? 'secondary'
  }
  return localStatusVariants[ip.status] ?? 'secondary'
}

export function IpPool() {
  const { data, isLoading, error } = useIpPool()

  if (error) {
    return (
      <div className="p-6">
        <Header title="IP Pool" />
        <div className="mt-6 rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-destructive">Failed to load IP pool: {error.message}</p>
        </div>
      </div>
    )
  }

  const summary = data?.summary

  return (
    <div>
      <Header title="IP Pool" description="Manage available IP addresses for rotation" />

      <div className="p-6 space-y-6">
        {/* Summary Cards - Two rows for cloud and local */}
        {summary && (
          <div className="space-y-4">
            {/* Cloud Pool Summary */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Cloud Pool (Tier 2)</h3>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Available</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-500">{summary.available}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Reserved</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-500">{summary.reserved}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Assigned</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{summary.assigned}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Blacklisted</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-500">{summary.blacklisted}</div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Local Pool Summary */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Local IPv6 Pool (Tier 1)</h3>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Ready</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-500">{summary.ready}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Pending</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-500">{summary.pending}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Active</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-500">{summary.active}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Blocked</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-500">{summary.blocked}</div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* IP List */}
        <Card>
          <CardHeader>
            <CardTitle>All IP Addresses ({data?.summary.total ?? 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-muted-foreground">Loading...</div>
            ) : data?.ips.length === 0 ? (
              <div className="text-muted-foreground">No IPs in pool</div>
            ) : (
              <div className="space-y-2">
                {data?.ips.map((ip) => (
                  <div
                    key={ip.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm break-all">{ip.ip_address}</span>
                      <Badge variant="outline" className="text-xs">
                        {ip.source === 'cloud' ? 'Cloud' : 'Local'}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        IPv{ip.ip_version}
                      </Badge>
                      <Badge variant={getStatusVariant(ip)} className="text-xs">
                        {ip.status}
                      </Badge>
                      {ip.protocol && (
                        <Badge variant="default" className="text-xs bg-blue-500">
                          {ip.protocol}
                        </Badge>
                      )}
                      {ip.risk_level && (
                        <Badge variant={riskVariants[ip.risk_level] ?? 'secondary'} className="text-xs">
                          {ip.risk_level}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {ip.purity_score !== null && (
                        <span>Purity: {ip.purity_score}</span>
                      )}
                      {ip.node_name && (
                        <span className="font-medium">{ip.node_name}</span>
                      )}
                      {ip.verified_at && (
                        <span>Verified: {formatRelativeTime(ip.verified_at)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
